import { cn } from '@veranda/shared/lib';
import { type VariantProps, cva } from 'class-variance-authority';
import { LoaderIcon } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-clash font-semibold uppercase transition-all duration-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          '!text-white border border-primary hf:border-secondary bg-primary hf:bg-secondary hf:text-white',
        'primary-outline':
          'border border-brand-burgundy hf:border-secondary bg-transparent hf:bg-secondary hf:text-white text-brand-burgundy',
        secondary:
          '!text-white border border-brand-orange bg-brand-orange hover:bg-brand-orange/90',
        'secondary-outline':
          'border border-secondary bg-transparent text-secondary hover:bg-secondary/10',
        tertiary:
          'border-brand-off-white bg-brand-off-brand-white hf:bg-secondary hf:text-white text-primary',
        ghost: 'text-brand-burgundy hover:bg-brand-burgundy/10',
        link: 'text-brand-burgundy underline-offset-4 hover:underline',
        icon: 'size-10 p-2 text-brand-burgundy hover:bg-brand-burgundy/10',
      },
      size: {
        sm: 'h-[40px] whitespace-nowrap border px-[26px] py-[14px] text-sm/none tracking-[0.01em]', // 14px font
        md: 'whitespace-nowrap px-10 py-4 text-base/none tracking-[0.03em]', // 16px font
        lg: 'px-12 py-4 text-[24px]/none tracking-[-0.02em]', // 20px font
        xl: 'h-[64px] px-10 py-5 text-[20px]/none tracking-[0.03em]',
        icon: 'size-10 p-2',
        // Extra variants from settings
        extra1:
          'h-[48px] border-0 px-8 py-4 text-base normal-case leading-[1.4] tracking-[0]',
        extra2:
          'h-[56px] border-4 px-10 py-4 font-extrabold text-2xl leading-[1.4] tracking-[0.03em]',
        extra3:
          'h-[48px] border-0 px-10 py-4 text-base normal-case leading-[1.4] tracking-[1px]',
        extra4:
          'h-[48px] border-0 px-9 py-3 font-normal text-2xl leading-[1.4] tracking-[0]',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      rounded: 'none',
    },
  }
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot.Root : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, rounded, className }),
          'relative'
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <span className="invisible flex items-center gap-2">
              {children}
            </span>
            <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2">
              <LoaderIcon className="size-5 animate-spin" />
            </div>
          </>
        ) : (
          <span className="flex items-center gap-2">{children}</span>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
